/** @jsxRuntime classic */
/** @jsx jsx */

import { labelStyles } from '@zapier/style-encapsulation';
import { css, jsx } from '@emotion/react';
import { useTrackEvent } from 'tracking/useTrackEvent';
import {
  Button,
  Spinner,
  TextInput,
  SubscriptionForm,
  ButtonProps,
  TextInputProps,
} from '@zapier/design-system';
import {
  ErrorMessage,
  SuccessMessage,
} from 'components/Subscribe/FlashMessages';

const Styles = labelStyles('SubscribeForm', {
  formWrapper: css`
    form > div {
      gap: 20px !important;
      // This is a special case to make sure there is a proper gap between 660px and 768px
      @media (min-width: 768px) {
        gap: 10px !important;
      }
    }
  `,
  btnWrapper: [
    css`
      button {
        width: 175px !important;
      }
    `,
  ],
  textInputWrapper: [
    css`
      width: 275px;
      text-align: unset;
    `,
  ],
});

export type Props = {
  buttonColor?: 'primary-alt' | 'primary';
  loading: boolean;
  error: boolean;
  sectionToTrack: string;
  success: boolean;
  onSubmit: (email: string) => void;
};

let intentFired: Boolean = false;
let confirmFired: Boolean = false;
export const SubscribeForm = ({
  buttonColor,
  onSubmit,
  loading,
  error,
  sectionToTrack,
  success,
}: Props) => {
  const trackEvent = useTrackEvent();

  if (loading) {
    return <Spinner size="small" />;
  }

  const fireIntentEvent = () => {
    if (!intentFired) {
      trackEvent('call_to_action', {
        path: window.location.pathname,
        from_path: document.referrer,
        cta_name: 'email_subscribe_intent',
        cta_raw_text: 'get productivity tips delivered straight to your inbox',
        cta_location: sectionToTrack,
        cta_goal: 'email_subscribe',
        link_destination: '',
        signup_type: '',
      });
      intentFired = true;
    }
  };

  if (error) {
    return (
      <ErrorMessage>Oops! We had trouble subscribing this email.</ErrorMessage>
    );
  }

  if (success) {
    if (!confirmFired) {
      trackEvent('call_to_action', {
        path: window.location.pathname,
        from_path: document.referrer,
        cta_name: 'email_subscribe_confirm',
        cta_raw_text: 'email address submitted',
        cta_location: sectionToTrack,
        cta_goal: 'email_subscribe',
        link_destination: '',
        signup_type: '',
      });
      confirmFired = true;
    }

    return (
      <SuccessMessage>Excellent! You have been subscribed.</SuccessMessage>
    );
  }

  return (
    <div css={Styles.formWrapper}>
      <SubscriptionForm
        placeholderText="Email address"
        renderButton={(buttonProps: ButtonProps) => (
          <div css={Styles.btnWrapper}>
            <Button
              {...buttonProps}
              type="submit"
              color={buttonColor}
              hasShadow={true}
              size="medium"
              isFullWidth={false}
            >
              Subscribe
            </Button>
          </div>
        )}
        renderInput={(inputProps: TextInputProps) => (
          <div css={Styles.textInputWrapper}>
            <TextInput
              {...inputProps}
              onFocus={fireIntentEvent}
              size="medium"
              type="email"
              isRequired={true}
            />
          </div>
        )}
        onSubmit={onSubmit}
      />
    </div>
  );
};
